import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useEditObjectMutation } from '_common/services/api/ObjectApi';

import { Heading, Input, Tooltip } from '@ets/design-system';
import { useIEnvision } from '_common/hooks';

type EditableNameProps = {
  object: doDOC.BaseObject;
  isRenaming?: boolean;
  onRenamingChange?: (payload: boolean) => void;
};

const EditableName = ({ object, isRenaming = false, onRenamingChange }: EditableNameProps) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editObject] = useEditObjectMutation();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(object.name);
  const [hovering, setHovering] = useState(false);
  const isIEnvision = useIEnvision();
  const disabled = (['admin', 'owner', 'edit'] as const).every(
    (permission) => !object.user_permissions.includes(permission),
  );

  useLayoutEffect(() => {
    if (isRenaming) {
      startRenaming();
    } else {
      setRenaming(false);
    }
  }, [isRenaming]);

  useEffect(() => {
    onRenamingChange?.(renaming);
  }, [renaming]);

  const startRenaming = () => {
    if (!disabled) {
      setRenaming(true);
      setHovering(false);
    }
  };

  const validation = useMemo(() => {
    const invalidCharactersRegex = /[<>"\\:*?/|]/;

    if (name.length === 0) {
      return intl.formatMessage({ id: 'validation.name.insertName' });
    } else if (invalidCharactersRegex.test(name)) {
      return intl.formatMessage({ id: 'RENAME_FILE_INPUT_ERROR' });
    } else if (name.length > 146) {
      return intl.formatMessage({ id: 'DOCUMENT_TITLE_LIMIT_CHARACTER' });
    }
    return '';
  }, [name]);

  const handleRename = () => {
    setRenaming(false);
    if (validation) {
      setName(object.name);
    } else if (name !== object.name) {
      editObject({ object_id: object.id, object_type: object.type, name });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    }
  };

  return (
    <div
      className={` ${
        renaming
          ? 'visible mr-8 inline-block max-w-300'
          : `flex h-16 min-w-0 cursor-default items-center break-all text-neutral-100 hover:rounded-lg hover:shadow-[inset_0_0_0_1px_var(--colors-neutral-20)]`
      } ${!isIEnvision ? 'mr-12' : 'mr-4'} `}
      onClick={startRenaming}
      data-testid="name-container"
    >
      {renaming ? (
        <Tooltip content={validation} open={!!validation} testId="rename-validations">
          <div>
            <Input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleRename}
              hasError={!!validation}
              size="m"
              placeholder=""
              isClearVisible={true}
              testId="name"
              autoFocus
            />
          </div>
        </Tooltip>
      ) : (
        <Tooltip
          content={intl.formatMessage({ id: 'RENAME_FILE' })}
          testId="rename"
          open={true}
          visible={hovering && !disabled}
        >
          <Heading
            className="max-w-300 overflow-hidden px-4 text-ellipsis whitespace-nowrap"
            size="s"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            testId="name-tooltip-reference"
          >
            {object.name}
          </Heading>
        </Tooltip>
      )}
    </div>
  );
};

export default EditableName;
